import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Siren, AlertCircle, AlertTriangle, CheckCircle2, ChevronRight } from 'lucide-react';
import { useCan } from '@/lib/permissions';

const STATUS = {
  alert: { Icon: AlertCircle, color: 'text-rose-400', dot: 'bg-rose-500' },
  warning: { Icon: AlertTriangle, color: 'text-amber-400', dot: 'bg-amber-500' },
  ok: { Icon: CheckCircle2, color: 'text-emerald-400', dot: 'bg-emerald-500' },
};

/** Home-dashboard tile: flags which models are currently signalling risk.
 * Renders nothing when the viewer can't manage governance or there are no runs. */
export default function RiskWatchlistTile() {
  const canView = useCan('glossary:read');
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!canView) return;
    api.getRiskWatchlist().then((r) => setItems(r.items || [])).catch(() => setItems([]));
  }, [canView]);

  if (!canView || !items || items.length === 0) return null;

  return (
    <div data-testid="risk-watchlist-tile">
      <h2 className="text-sm font-heading font-semibold text-foreground flex items-center gap-2 mb-3">
        <Siren className="w-4 h-4 text-rose-400" /> Risk Watchlist
      </h2>
      <div className="space-y-2">
        {items.map((it) => {
          const st = STATUS[it.status] || STATUS.ok;
          const StIcon = st.Icon;
          return (
            <button key={it.model_id} onClick={() => navigate(`/admin/risk-signals?model_id=${encodeURIComponent(it.model_id)}`)}
              className="w-full text-left glass-card rounded-xl p-3 hover:border-primary/30 transition-all group flex items-center gap-3"
              data-testid={`risk-watch-${it.model_id}`}>
              <span className={`w-2 h-2 rounded-full ${st.dot} shrink-0`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{it.model_name}</p>
                <p className="text-[11px] text-muted-foreground truncate">
                  {it.top_headline || 'No active signals'}
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {it.alert > 0 && <Badge variant="outline" className="text-[9px] bg-rose-500/10 border-rose-500/40 text-rose-600 dark:text-rose-300">{it.alert} alert</Badge>}
                {it.warning > 0 && <Badge variant="outline" className="text-[9px] bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-300">{it.warning} warn</Badge>}
                <StIcon className={`w-4 h-4 ${st.color}`} />
                <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary transition-colors" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export { RiskWatchlistTile };
